<script setup lang="ts">
import { getTranslatedProperty, getProductRoute } from "@shopware/helpers"
import type { Schemas } from "#shopware"

interface ProductCustomFields {
    gisy_store_only?: boolean
    care_products?: string
    gisy_displayable_product_number?: string
}

const { apiClient } = useShopwareContext()

const product = defineModel<Schemas["Product"]>('product', { required: true })

const navigationItems = inject<Ref<Schemas['NavigationRouteResponse'] | null>>('mainNavigation', ref(null))

const props = defineProps<{
    configurator: Schemas["PropertyGroup"][] | null
}>()

const { addToCart, quantity } = useAddToCart(product)
const { showProductAdded } = useCartProductNotification()
const { addToClickAndReserve, isInClickAndReserve } = useClickAndReserve()
const { success, error, warning } = useGlobalNotifications()
const { t } = useI18n()
const { metaKey } = useThemeMeta()

// For product route formatting
const localePath = useLocalePath()
const { formatLink } = useInternationalization(localePath)

// Get Nuxt app instance
const nuxtApp = useNuxtApp()

const variantNotFound = ref(false)

const isBuyable = computed(() => {
    if (product.value?.calculatedMaxPurchase) {
        return product.value.available && (!product.value.childCount || product.value.childCount <= 0) && product.value.calculatedMaxPurchase > 0 && !variantNotFound.value
    } else return false
})

const inStoreOnly = computed(() => {
    return (product.value?.customFields as ProductCustomFields)?.gisy_store_only === true
})

const displayableProductNumber = computed(() => {
    return (product.value?.customFields as ProductCustomFields)?.gisy_displayable_product_number
})

const currentProductColor = computed(() => {
    const colorProp = product.value?.properties?.find(
        (p: Schemas['PropertyGroupOption']) =>
            (p.group?.translated?.name || p.group?.name || '').toLowerCase() === 'farbe'
    )
    return colorProp?.translated?.name || colorProp?.name || ''
})

const currentProductColorId = computed(() => {
    const colorProp = product.value?.properties?.find(
        (p: Schemas['PropertyGroupOption']) =>
            (p.group?.translated?.name || p.group?.name || '').toLowerCase() === 'farbe'
    )
    return colorProp?.id || ''
})

const { open } = useSidenav()

// Helper function to get variant names from selected options
const getVariantName = () => {
    if (!product.value?.optionIds || !props.configurator || product.value.optionIds.length === 0) {
        return undefined
    }

    const variantNames: string[] = []

    // Loop through all configurator groups
    props.configurator.forEach((group: Schemas['PropertyGroup']) => {
        // Loop through options in each group
        group.options?.forEach((option: Schemas['PropertyGroupOption']) => {
            // Check if this option ID is in the product's selected optionIds
            if (product.value?.optionIds?.includes(option.id)) {
                variantNames.push(option.name || option.translated?.name || '')
            }
        })
    })

    return variantNames.length > 0 ? variantNames.join(' / ') : undefined
}

// Helper function to get GTM product data
const getGtmProductData = () => {
    const price = product.value?.calculatedPrice?.unitPrice || 0
    const productName = getTranslatedProperty(product.value, 'name') || ''

    return {
        id: product.value?.id,
        name: productName,
        price: price,
        brand: product.value?.manufacturer?.name,
        category: product.value?.categories?.[0]?.name,
        variant: getVariantName()
    }
}

// Add product to cart, show notification, and open cart sidenav
const handleAddToCart = async () => {
    const cartResponse = await addToCart()

    if (cartResponse.errors && typeof cartResponse.errors === 'object') {
        type CartErrorExtended = Schemas['CartError'] & { translatedMessage?: string }
        const entries = Object.values(cartResponse.errors) as CartErrorExtended[]
        const getMessage = (e: CartErrorExtended) => e.translatedMessage || e.message

        const levelErrors = entries.filter((e) => e.level === 20)
        const levelWarnings = entries.filter((e) => e.level === 10)

        if (levelWarnings.length > 0) {
            levelWarnings.forEach((e) => warning(getMessage(e)))
            return
        }

        if (levelErrors.length > 0) {
            levelErrors.forEach((e) => error(getMessage(e)))
            return
        }
    }

    // Track add to cart event using hook
    nuxtApp.callHook('nuxtware:productCard:clicked', {
        product: getGtmProductData(),
        quantity: quantity.value
    })

    showProductAdded()
    open('cart')
}

// Add product to click and reserve list and open sidenav
const handleClickAndReserve = () => {
    if (product.value && !isInClickAndReserve(product.value.id)) {
        addToClickAndReserve(product.value.id)
        success(t('clickAndReserve.messages.addedToClickAndReserve', { product: product.value.translated?.name || '' }))
    }
    open('clickAndReserve')
}

function updateProduct (newProduct: Schemas["Product"]) {
    product.value = {
        ...newProduct,
        // Preserve fields that might not be included in variant response
        // to prevent flickering/disappearing content during variant changes
        manufacturer: newProduct.manufacturer || product.value?.manufacturer,
        cover: newProduct.cover || product.value?.cover,
        media: newProduct.media?.length ? newProduct.media : product.value?.media,
        properties: newProduct.properties?.length ? newProduct.properties : product.value?.properties,
        crossSellings: newProduct.crossSellings?.length ? newProduct.crossSellings : product.value?.crossSellings,
    } as Schemas["Product"]
    variantNotFound.value = false
}

function handleVariantNotFound () {
    variantNotFound.value = true
}

// Use the composable for all structured data logic
const { jsonLd } = useProductStructuredData(product)

// Build meta description
const metaDescription = computed(() => {
    const productName = getTranslatedProperty(product.value, 'name') || ''

    // Check if product has custom meta description
    if (product.value?.translated?.metaDescription) {
        return product.value.translated.metaDescription
    }
    // Use template with product name if available
    else if (productName) {
        return t(`${metaKey}.product.metaDescription`, { productName })
    }
    // Fallback
    else {
        return t(`${metaKey}.product.metaDescriptionFallback`)
    }
})

// Build meta title
const metaTitle = computed(() => {
    const productName = getTranslatedProperty(product.value, 'name') || ''

    // Check if product has custom meta title
    if (product.value?.translated?.metaTitle) {
        return product.value.translated.metaTitle
    }
    // Use template with product name if available
    else if (productName) {
        return t(`${metaKey}.product.title`, { productName })
    }
    // Fallback
    else {
        return t(`${metaKey}.product.titleFallback`)
    }
})

// Color Variants
const hasColorVariantsCrossSelling = computed(() => {
    const crossSellings = product.value?.crossSellings
    if (!crossSellings || crossSellings.length === 0) return false
    return crossSellings.some((cs) => {
        const name = cs.translated?.name || cs.name || ''
        return name.toLowerCase() === 'farbvarianten'
    })
})

const crossSellings = ref<Schemas["CrossSellingElement"][] | null>(null)

const fetchCrossSellings = async () => {
    if (!hasColorVariantsCrossSelling.value) return

    const response = await apiClient.invoke(
        'readProductCrossSellings post /product/{productId}/cross-selling',
        {
            pathParams: { productId: product.value!.id },
            headers: {
                'sw-include-seo-urls': true
            }
        }
    )

    // Filter to only return "Farbvarianten" collection
    const colorVariantsCollection = response.data?.filter((collection: Schemas["CrossSellingElement"]) => {
        const name = collection.crossSelling?.translated?.name || collection.crossSelling?.name || ''
        return name.toLowerCase() === 'farbvarianten'
    }) || []

    crossSellings.value = colorVariantsCollection.length > 0 ? colorVariantsCollection : null
}


const mainCategoryName = computed(() => {
    return product.value?.seoCategory?.translated?.name || product.value?.seoCategory?.name || ''
})

const categorySeoPath = computed(() => product.value?.seoCategory?.seoUrls?.[0]?.seoPathInfo || '')

const colorFilterLink = computed(() => {
    if (!categorySeoPath.value || !currentProductColorId.value) return ''
    return `/${categorySeoPath.value}?properties=${currentProductColorId.value}`
})

const manufacturerFilterLink = computed(() => {
    if (!categorySeoPath.value || !product.value?.manufacturerId) return ''
    return `/${categorySeoPath.value}?manufacturer=${product.value.manufacturerId}`
})

const manufacturerGenderLink = computed(() => {
    const zielgruppeProp = product.value?.properties?.find(
        (p: Schemas['PropertyGroupOption']) =>
            (p.group?.translated?.name || p.group?.name || '').toLowerCase() === 'zielgruppe'
    )
    const zielgruppe = (zielgruppeProp?.translated?.name || zielgruppeProp?.name || '').toLowerCase()

    const genderMap: Record<string, string> = {
        herren: 'herrenschuhe',
        damen: 'damenschuhe',
        kinder: 'kinderschuhe',
    }

    const genderSlug = genderMap[zielgruppe]
    if (!genderSlug) return null

    const manufacturerName = product.value?.manufacturer?.name?.toLowerCase()
    if (!manufacturerName) return null

    // Search only within the Marken category's children for a match by manufacturer name
    const allCategories = (navigationItems.value as Schemas['Category'][] | null) ?? []
    const markenCategory = allCategories.find(cat => cat.name === 'Marken')
    if (!markenCategory) return null

    const markenChildren = (markenCategory.children as Schemas['Category'][]) ?? []
    const manufacturerCategory = markenChildren.find(c => c.name?.toLowerCase() === manufacturerName) ?? null
    if (!manufacturerCategory) return null

    const seoPath = (manufacturerCategory.seoUrls as Array<{ seoPathInfo: string }> | undefined)?.[0]?.seoPathInfo ?? ''
    const manufacturerSlug = seoPath.replace(/\/$/, '')
    if (!manufacturerSlug) return null

    return `/${manufacturerSlug}/${genderSlug}`
})
// Parse the article numbers from the comma-separated string
const careProductNumbers = computed(() => {
    const careString = (product.value?.customFields as ProductCustomFields)?.care_products
    if (!careString) return []
    return careString.split(',').map(num => num.trim()).filter(Boolean)
})

// State for fetched care products
const careProductsData = ref<Schemas['Product'][]>([])
const careProductsLoading = ref(false)
const careProductsLoaded = ref(false)

// Fetch care products by article numbers
const fetchCareProducts = async () => {
    // Skip if already loaded, currently loading, or no article numbers
    if (careProductsLoaded.value || careProductsLoading.value || careProductNumbers.value.length === 0) {
        return
    }

    careProductsLoading.value = true

    try {
        const response = await apiClient.invoke('readProduct post /product', {
            headers: {
                'sw-include-seo-urls': true
            },
            body: {
                filter: [
                    {
                        type: 'equalsAny',
                        field: 'productNumber',
                        value: careProductNumbers.value
                    }
                ],
                includes: {
                    product: [
                        'id',
                        'name',
                        'translated',
                        'productNumber',
                        'calculatedPrice',
                        'cover',
                        'seoUrls'
                    ]
                }
            }
        })

        careProductsData.value = response.data.elements || []
        careProductsLoaded.value = true
    } catch (error) {
        console.error('Failed to fetch care products:', error)
    } finally {
        careProductsLoading.value = false
    }
}

const handleAccordionToggle = (id: string, isOpen: boolean) => {
    if (id === 'care-recommendation' && isOpen) {
        fetchCareProducts()
    }
}

const productName = computed(() => {
    let name = getTranslatedProperty(product.value, 'name')

    if(product.value?.manufacturer?.name) {
        name = name?.replace(product.value.manufacturer.name, '')
    }

    return name
})

// Add JSON-LD to head
useHead({
    title: metaTitle,
    meta: [
        { name: 'description', content: metaDescription },
        { name: 'og:description', content: metaDescription },
        { name: 'og:type', content: 'product' }
    ],
    script: [
        {
            type: 'application/ld+json',
            innerHTML: jsonLd
        }
    ]
})

// Track product view and fetch cross-sellings when component mounts
onMounted(() => {
    nuxtApp.callHook('nuxtware:productDetailBuyBox:viewed', {
        product: getGtmProductData()
    })
    fetchCrossSellings()
})
</script>
<template>
    <div class="h-full w-full lg:px-8 lg:py-12">
        <div class="flex flex-col justify-start gap-4">
            <!-- Product Headline Section -->
            <section class="flex flex-row justify-between items-start gap-4 pb-6 mb-4 border-b border-border">
                <div class="basis-2/3">
                    <h1 class="flex flex-col gap-1">
                        <!-- Manufacturer Name (Large) -->
                        <span
                            v-if="product.manufacturer?.name"
                            class="text-lg font-bold uppercase min-h-[28px]"
                        >
                            {{ product.manufacturer.name }}
                        </span>

                        <!-- Product Name (Small) -->
                        <span class="text-base font-normal">
                            {{ productName }}
                        </span>
                    </h1>

                    <!-- Article Number -->
                    <div v-if="displayableProductNumber" class="text-xs mt-2">
                        {{ $t('product.productNumber') }}: {{ displayableProductNumber }}
                    </div>
                </div>

                <!-- Manufacturer Logo -->
                <div v-if="product.manufacturer?.media?.url" class="basis-1/3 justify-items-end">
                    <FoundationLink
                        v-if="manufacturerGenderLink"
                        :href="manufacturerGenderLink"
                    >
                        <FoundationImage
                            :src="product.manufacturer.media.url"
                            :alt="product.manufacturer.name"
                            :title="product.manufacturer.name"
                            class="h-auto w-full max-w-[160px] object-contain"
                            width="160"
                            loading="lazy"
                        />
                    </FoundationLink>
                    <FoundationImage
                        v-else
                        :src="product.manufacturer.media.url"
                        :alt="product.manufacturer.name"
                        :title="product.manufacturer.name"
                        class="h-auto w-full max-w-[160px] object-contain"
                        width="160"
                        loading="lazy"
                    />
                </div>
            </section>

            <!-- Color Variant Selection -->
            <section v-if="hasColorVariantsCrossSelling" class="flex flex-col">
                <FoundationHeadline tag="h6" class="font-bold mb-3">
                    {{ $t('product.colorVariant.currentColor') }}
                    <span class="font-normal">{{ currentProductColor }}</span>
                </FoundationHeadline>

                <FoundationHeadline tag="h6" class="font-bold">
                    {{ $t('product.colorVariant.title') }}
                </FoundationHeadline>

                <!-- Skeleton while loading -->
                <div v-if="!crossSellings" class="flex flex-wrap gap-3 mt-5">
                    <div class="w-13 h-10 border border-border rounded bg-base-200 animate-pulse" />
                </div>

                <!-- Loaded color variants -->
                <template v-for="collection in crossSellings" v-else :key="collection.crossSelling?.id">
                    <div class="flex flex-wrap gap-3 mt-5">
                        <FoundationLink
                            v-for="crossProduct in collection.products"
                            :key="crossProduct.id"
                            :href="formatLink(getProductRoute(crossProduct))"
                            type="internal"
                            class="block w-13 h-10 border border-border rounded overflow-hidden hover:border-primary transition-colors"
                        >
                            <FoundationImage
                                v-if="crossProduct.cover?.media?.url"
                                :src="crossProduct.cover.media.url"
                                :alt="crossProduct.translated?.name || crossProduct.name || ''"
                                class="w-full h-full object-cover p-1"
                                width="42"
                                loading="lazy"
                            />

                            <div v-else class="h-full flex flex-col justify-center items-center bg-base-300 overflow-hidden text-base-500">
                                <FoundationIcon name="image" size="md" />
                            </div>
                        </FoundationLink>
                    </div>
                </template>
            </section>

            <!-- Variant Selection -->
            <section>
                <ProductDetailVariants
                    :configurator="props.configurator"
                    :parent-id="product?.parentId"
                    :current-product="product"
                    @product-variant-changed="(newProduct) => updateProduct(newProduct)"
                    @variant-not-found="handleVariantNotFound"
                />
            </section>

            <!-- Price & Cart Actions Section -->
            <section class="flex flex-col gap-5 mb-2">
                <!-- Price Box with Wishlist -->
                <div class="flex flex-row items-center justify-between gap-4">
                    <div class="flex flex-col gap-2">
                        <ProductPrice
                            :product="product"
                            :product-number="displayableProductNumber"
                            alignment="start"
                            :show-tier-prices="true"
                            :show-tax="true"
                        />
                    </div>

                    <!-- Wishlist Toggle -->
                    <ProductWishlistToggle
                        :product="product"
                        variant="icon"
                        class="shrink-0 hidden lg:inline-flex"
                    />
                </div>

                <template v-if="isBuyable">
                    <!-- Cart Button with Quantity Selector -->
                    <div class="flex flex-row gap-2 items-center">
                        <FoundationButton
                            v-if="!inStoreOnly"
                            size="large"
                            color="primary"
                            class="w-full flex items-center justify-between gap-2"
                            @click="handleAddToCart"
                        >
                            <FoundationIcon
                                name="cart"
                                class="w-5 h-5"
                                aria-hidden="true"
                            />
                            <span class="font-semibold">{{ $t("product.addToCart") }}</span>
                            <span class="w-5" />
                        </FoundationButton>
                        <span
                            v-else
                            class="w-full flex items-center justify-center border border-border h-13"
                        >
                            {{ $t("product.inStoreOnly") }}
                        </span>
                    </div>

                    <!-- Click & Reserve Button -->
                    <FoundationButton
                        size="large"
                        color="primary"
                        class="w-full flex items-center justify-between gap-2"
                        @click="handleClickAndReserve"
                    >
                        <FoundationIcon
                            name="map-pin"
                            class="w-5 h-5"
                            aria-hidden="true"
                        />
                        <span class="font-semibold">{{ $t("product.clickAndReserve") }}</span>
                        <span class="w-5" />
                    </FoundationButton>
                </template>

                <NotificationItem 
                    v-else 
                    type="error" 
                    variant="inline" 
                    :dismissible="false"
                >
                    <span>{{ $t("product.notAvailable") }}</span>
                </NotificationItem>
            </section>

            <!-- Accordion Tabs Section -->
            <section class="flex flex-col">
                <ComponentAccordion
                    :items="[
                        { id: 'description', headline: $t('product.accordion.description') },
                        { id: 'shipping-details', headline: $t('product.accordion.shippingDetails') },
                        { id: 'care-recommendation', headline: $t('product.accordion.careRecommendation') }
                    ]"
                    headline-tag="h3"
                    headline-tag-class="text-base"
                    :has-border="false"
                    min-height="min-h-10"
                    type="single"
                    @toggle="handleAccordionToggle"
                >
                    <!-- Description Tab -->
                    <template #description>
                        <ProductDetailDescription :product="product" />
                    </template>

                    <!-- Shipping Details Tab -->
                    <template #shipping-details>
                        <div class="pb-4">
                            <ProductDeliveryInformation :product="product" />

                            <LazyProductPurchaseUnitAndInfos
                                v-if="product?.purchaseUnit"
                                :product="product"
                                :show-product-info="true"
                            />
                        </div>
                    </template>

                    <!-- Care Recommendation Tab -->
                    <template #care-recommendation>
                        <div class="pb-4">
                            <!-- Care products list / skeleton -->
                            <div v-if="careProductsLoading || careProductsData.length > 0" class="flex gap-3">
                                <!-- Skeleton placeholder while loading -->
                                <template v-if="careProductsLoading">
                                    <div class="w-24 h-20 bg-base-200 border border-border animate-pulse" />
                                </template>

                                <!-- Loaded care products -->
                                <template v-else>
                                    <FoundationLink
                                        v-for="careProduct in careProductsData"
                                        :key="careProduct.id"
                                        :href="formatLink(getProductRoute(careProduct))"
                                        type="internal"
                                        class="flex items-center justify-center w-24 h-20 bg-base-200 border border-border hover:border-primary transition-colors"
                                    >
                                        <FoundationImage
                                            v-if="careProduct.cover?.media?.url"
                                            :src="careProduct.cover.media.url"
                                            :alt="careProduct.translated?.name || careProduct.name"
                                            class="max-w-20 max-h-12"
                                            height="48"
                                            fit="auto"
                                        />
                                    </FoundationLink>
                                </template>
                            </div>

                            <FoundationLink
                                href="/pflegemittel"
                                type="internal"
                                class="text-sm mt-4 inline-block underline"
                            >
                                {{ $t('product.accordion.careProductsLink') }}
                            </FoundationLink>
                        </div>
                    </template>
                </ComponentAccordion>
            </section>

            <!-- Filtered Product Links -->
            <section v-if="manufacturerFilterLink || colorFilterLink" class="flex justify-between mt-4">

                <FoundationLink
                    v-if="manufacturerFilterLink"
                    :href="manufacturerFilterLink"
                    type="internal"
                    class="text-sm underline text-primary"
                >
                    {{ $t('product.links.allManufacturerProducts', { manufacturer: product.manufacturer?.name }) }} {{ mainCategoryName }}
                </FoundationLink>

                <FoundationLink
                    v-if="colorFilterLink"
                    :href="colorFilterLink"
                    type="internal"
                    class="text-sm underline text-primary"
                >
                    {{ $t('product.links.moreInColor', { category: mainCategoryName, color: currentProductColor }) }}
                </FoundationLink>

            </section>
        </div>
    </div>
</template>
